import { useState, useEffect } from 'react';
import { paymentApi, studentApi, feeApi, schoolYearApi } from '../lib/api';
import { usePermissions } from '../components/Layout';
import {
  Plus,
  Search,
  Filter,
  Wallet,
  CheckCircle2,
  Clock,
  AlertCircle,
  TrendingUp,
  Download,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export default function PaymentsPage() {
  const { canCreate } = usePermissions();
  const [payments, setPayments] = useState([]);
  const [students, setStudents] = useState([]);
  const [fees, setFees] = useState([]);
  const [schoolYear, setSchoolYear] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showFeeDialog, setShowFeeDialog] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [newPayment, setNewPayment] = useState({
    student_id: '',
    fee_id: '',
    amount_due: 0,
    amount_paid: 0,
    discount: 0,
    discount_reason: '',
    payment_mode: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    month: '',
    notes: '',
    description: '',
    use_custom_amount: false,
  });
  const [newFee, setNewFee] = useState({
    name: '',
    amount: 0,
    fee_type: 'other',
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }

      const [paymentsRes, studentsRes, feesRes, yearRes] = await Promise.all([
        paymentApi.getAll(params),
        studentApi.getAll({}), // Charger tous les élèves (actifs et archivés) pour les noms
        feeApi.getAll({}),
        schoolYearApi.getActive(),
      ]);

      setPayments(paymentsRes.data);
      setStudents(studentsRes.data);
      setFees(feesRes.data);
      setSchoolYear(yearRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    
    // Validation: étudiant requis, et soit un type de frais soit un montant personnalisé
    if (!newPayment.student_id) {
      toast.error('Veuillez sélectionner un élève');
      return;
    }
    
    if (!newPayment.fee_id && !newPayment.use_custom_amount) {
      toast.error('Veuillez sélectionner un type de frais ou saisir un montant personnalisé');
      return;
    }
    
    if (newPayment.use_custom_amount && (!newPayment.amount_due || newPayment.amount_due <= 0)) {
      toast.error('Veuillez saisir un montant valide');
      return;
    }

    setFormLoading(true);
    try {
      // Calcul du montant après réduction
      const amountAfterDiscount = newPayment.amount_due - (newPayment.discount || 0);
      
      // Calcul du statut basé sur le montant après réduction
      const status = newPayment.amount_paid >= amountAfterDiscount 
        ? 'paid' 
        : newPayment.amount_paid > 0 
          ? 'partial' 
          : 'pending';

      const paymentData = {
        ...newPayment,
        school_year_id: schoolYear?.id || '',
        status,
      };
      
      // Si montant personnalisé, on n'envoie pas fee_id
      if (newPayment.use_custom_amount) {
        delete paymentData.fee_id;
      }
      delete paymentData.use_custom_amount;

      await paymentApi.create(paymentData);
      toast.success('Paiement enregistré');
      setShowAddDialog(false);
      resetPaymentForm();
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setFormLoading(false);
    }
  };

  const handleAddFee = async (e) => {
    e.preventDefault();
    if (!newFee.name || !newFee.amount) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setFormLoading(true);
    try {
      await feeApi.create({
        ...newFee,
        school_year_id: schoolYear?.id || '',
      });
      toast.success('Type de frais créé');
      setShowFeeDialog(false);
      setNewFee({ name: '', amount: 0, fee_type: 'other' });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setFormLoading(false);
    }
  };

  const resetPaymentForm = () => {
    setNewPayment({
      student_id: '',
      fee_id: '',
      amount_due: 0,
      amount_paid: 0,
      discount: 0,
      discount_reason: '',
      payment_mode: 'cash',
      payment_date: new Date().toISOString().split('T')[0],
      month: '',
      notes: '',
      description: '',
      use_custom_amount: false,
    });
  };

  const handleFeeSelect = (feeId) => {
    const fee = fees.find((f) => f.id === feeId);
    setNewPayment({
      ...newPayment,
      fee_id: feeId,
      amount_due: fee?.amount || 0,
      discount: 0,
      discount_reason: '',
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStatusInfo = (status) => {
    const statuses = {
      paid: { label: 'Payé', class: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      pending: { label: 'En attente', class: 'bg-amber-100 text-amber-700', icon: Clock },
      partial: { label: 'Partiel', class: 'bg-blue-100 text-blue-700', icon: AlertCircle },
      overdue: { label: 'En retard', class: 'bg-red-100 text-red-700', icon: AlertCircle },
    };
    return statuses[status] || statuses.pending;
  };

  const getStudentName = (studentId) => {
    if (!studentId) return 'Élève inconnu';
    const student = students.find((s) => s.id === studentId);
    return student ? `${student.first_name} ${student.last_name}` : 'Élève archivé';
  };

  const getFeeName = (feeId) => {
    if (!feeId) return null;
    const fee = fees.find((f) => f.id === feeId);
    return fee?.name || null;
  };

  // State pour le téléchargement de PDF en cours
  const [downloadingId, setDownloadingId] = useState(null);

  // Fonction pour télécharger le reçu PDF avec authentification
  const handleDownloadReceipt = async (paymentId) => {
    setDownloadingId(paymentId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/${paymentId}/receipt`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `recu_${paymentId.substring(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Reçu téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du reçu');
      console.error('Download error:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  // Calculate stats
  const stats = {
    total: payments.reduce((sum, p) => sum + (p.amount_paid || 0), 0),
    pending: payments
      .filter((p) => p.status !== 'paid')
      .reduce((sum, p) => sum + ((p.amount_due || 0) - (p.amount_paid || 0)), 0),
    paid: payments.filter((p) => p.status === 'paid').length,
    unpaid: payments.filter((p) => p.status !== 'paid').length,
  };

  const months = [
    { value: 'janvier', label: 'Janvier' },
    { value: 'février', label: 'Février' },
    { value: 'mars', label: 'Mars' },
    { value: 'avril', label: 'Avril' },
    { value: 'mai', label: 'Mai' },
    { value: 'juin', label: 'Juin' },
    { value: 'juillet', label: 'Juillet' },
    { value: 'août', label: 'Août' },
    { value: 'septembre', label: 'Septembre' },
    { value: 'octobre', label: 'Octobre' },
    { value: 'novembre', label: 'Novembre' },
    { value: 'décembre', label: 'Décembre' },
  ];

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Paiements</h1>
          <p className="text-muted-foreground text-sm">Consultez les paiements des élèves</p>
        </div>
        {canCreate('payments') && (
          <div className="flex gap-2">
            <Dialog open={showFeeDialog} onOpenChange={setShowFeeDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-full" data-testid="add-fee-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Type de frais
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Nouveau type de frais</DialogTitle>
                <DialogDescription>Définir un nouveau type de frais scolaires</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddFee} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom du frais *</Label>
                  <Input
                    value={newFee.name}
                    onChange={(e) => setNewFee({ ...newFee, name: e.target.value })}
                    placeholder="Ex: Mensualité, Inscription..."
                    data-testid="fee-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Montant (FCFA) *</Label>
                  <Input
                    type="number"
                    value={newFee.amount}
                    onChange={(e) => setNewFee({ ...newFee, amount: parseFloat(e.target.value) || 0 })}
                    placeholder="15000"
                    data-testid="fee-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={newFee.fee_type}
                    onValueChange={(value) => setNewFee({ ...newFee, fee_type: value })}
                  >
                    <SelectTrigger data-testid="fee-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="registration">Inscription</SelectItem>
                      <SelectItem value="monthly">Mensualité</SelectItem>
                      <SelectItem value="annual">Annuel</SelectItem>
                      <SelectItem value="transport">Transport</SelectItem>
                      <SelectItem value="uniform">Uniforme</SelectItem>
                      <SelectItem value="books">Fournitures</SelectItem>
                      <SelectItem value="meals">Restauration</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowFeeDialog(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" disabled={formLoading} data-testid="save-fee-btn">
                    {formLoading ? 'Création...' : 'Créer'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-payment-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau paiement
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Enregistrer un paiement</DialogTitle>
                <DialogDescription>Enregistrer le paiement d'un élève</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddPayment} className="space-y-4">
                <div className="space-y-2">
                  <Label>Élève *</Label>
                  <Select
                    value={newPayment.student_id}
                    onValueChange={(value) => setNewPayment({ ...newPayment, student_id: value })}
                  >
                    <SelectTrigger data-testid="payment-student">
                      <SelectValue placeholder="Sélectionner un élève" />
                    </SelectTrigger>
                    <SelectContent>
                      {students.map((student) => (
                        <SelectItem key={student.id} value={student.id}>
                          {student.first_name} {student.last_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {fees.length > 0 ? (
                  <>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Type de frais</Label>
                        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newPayment.use_custom_amount}
                            onChange={(e) => setNewPayment({ 
                              ...newPayment, 
                              use_custom_amount: e.target.checked,
                              fee_id: e.target.checked ? '' : newPayment.fee_id,
                              amount_due: e.target.checked ? 0 : newPayment.amount_due
                            })}
                            className="rounded"
                            data-testid="payment-custom-amount"
                          />
                          Montant personnalisé
                        </label>
                      </div>
                      {!newPayment.use_custom_amount ? (
                        <Select value={newPayment.fee_id} onValueChange={handleFeeSelect}>
                          <SelectTrigger data-testid="payment-fee">
                            <SelectValue placeholder="Sélectionner" />
                          </SelectTrigger>
                          <SelectContent>
                            {fees.map((fee) => (
                              <SelectItem key={fee.id} value={fee.id}>
                                {fee.name} ({formatCurrency(fee.amount)})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="space-y-3">
                          <Input
                            placeholder="Description du paiement"
                            value={newPayment.description}
                            onChange={(e) => setNewPayment({ ...newPayment, description: e.target.value })}
                            data-testid="payment-description"
                          />
                          <Input
                            type="number"
                            placeholder="Montant dû (FCFA)"
                            value={newPayment.amount_due || ''}
                            onChange={(e) => setNewPayment({ ...newPayment, amount_due: parseFloat(e.target.value) || 0 })}
                            data-testid="payment-amount-due"
                          />
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      Aucun type de frais défini. Vous pouvez créer un paiement avec un montant personnalisé.
                    </p>
                    <div className="space-y-2">
                      <Label>Description *</Label>
                      <Input
                        placeholder="Ex: Frais de scolarité, Inscription..."
                        value={newPayment.description}
                        onChange={(e) => setNewPayment({ ...newPayment, description: e.target.value, use_custom_amount: true })}
                        data-testid="payment-description-no-fees"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Montant dû (FCFA) *</Label>
                      <Input
                        type="number"
                        placeholder="15000"
                        value={newPayment.amount_due || ''}
                        onChange={(e) => setNewPayment({ ...newPayment, amount_due: parseFloat(e.target.value) || 0, use_custom_amount: true })}
                        data-testid="payment-amount-due-no-fees"
                      />
                    </div>
                  </div>
                )}

                {/* Section Réduction */}
                {newPayment.amount_due > 0 && (
                  <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertCircle className="w-4 h-4" />
                      <Label className="text-amber-700 font-medium">Réduction permanente (optionnel)</Label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Montant réduction</Label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={newPayment.discount || ''}
                          onChange={(e) => setNewPayment({ ...newPayment, discount: parseFloat(e.target.value) || 0 })}
                          data-testid="payment-discount"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Raison</Label>
                        <Input
                          placeholder="Ex: Bourse, famille nombreuse..."
                          value={newPayment.discount_reason}
                          onChange={(e) => setNewPayment({ ...newPayment, discount_reason: e.target.value })}
                          data-testid="payment-discount-reason"
                        />
                      </div>
                    </div>
                    {newPayment.discount > 0 && (
                      <div className="text-sm text-amber-700 bg-amber-100 p-2 rounded">
                        <div className="flex justify-between">
                          <span>Montant original:</span>
                          <span className="line-through">{formatCurrency(newPayment.amount_due)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Réduction:</span>
                          <span className="text-red-600">- {formatCurrency(newPayment.discount)}</span>
                        </div>
                        <div className="flex justify-between font-bold border-t border-amber-300 pt-1 mt-1">
                          <span>À payer:</span>
                          <span className="text-green-700">{formatCurrency(newPayment.amount_due - newPayment.discount)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Montant payé (FCFA) *</Label>
                  <Input
                    type="number"
                    value={newPayment.amount_paid}
                    onChange={(e) => setNewPayment({ ...newPayment, amount_paid: parseFloat(e.target.value) || 0 })}
                    placeholder="Ex: 15000"
                    data-testid="payment-paid"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Mode de paiement</Label>
                  <Select
                    value={newPayment.payment_mode}
                    onValueChange={(value) => setNewPayment({ ...newPayment, payment_mode: value })}
                  >
                    <SelectTrigger data-testid="payment-mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Espèces</SelectItem>
                      <SelectItem value="mobile_money">Mobile Money</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                    Annuler
                  </Button>
                  <Button type="submit" disabled={formLoading} data-testid="save-payment-btn">
                    {formLoading ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xl font-bold font-heading">{formatCurrency(stats.total)}</p>
              <p className="text-xs text-muted-foreground">Total collecté</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-bold font-heading">{formatCurrency(stats.pending)}</p>
              <p className="text-xs text-muted-foreground">Impayés</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{stats.paid}</p>
              <p className="text-xs text-muted-foreground">Payés</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold font-heading">{stats.unpaid}</p>
              <p className="text-xs text-muted-foreground">Non payés</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-48 rounded-full" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="paid">Payés</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="partial">Partiels</SelectItem>
                <SelectItem value="overdue">En retard</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12">
              <Wallet className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground mb-4">Aucun paiement enregistré</p>
              <Button variant="outline" className="rounded-full" onClick={() => setShowAddDialog(true)}>
                Enregistrer un paiement
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Élève</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Payé</TableHead>
                  <TableHead className="text-center">Statut</TableHead>
                  <TableHead className="text-center">Reçu</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => {
                  const statusInfo = getStatusInfo(payment.status);
                  const StatusIcon = statusInfo.icon;
                  const hasDiscount = Boolean(payment.discount) && payment.discount > 0;
                  return (
                    <TableRow key={payment.id} className="table-row-hover">
                      <TableCell>
                        <p className="font-medium">{getStudentName(payment.student_id)}</p>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        <div>{getFeeName(payment.fee_id) || payment.description || 'Frais'}</div>
                        {hasDiscount && (
                          <div className="text-xs text-amber-600 mt-1">
                            🏷️ Réduction: {formatCurrency(payment.discount)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {hasDiscount && (
                          <p className="text-xs text-muted-foreground line-through">
                            {formatCurrency(payment.amount_due || 0)}
                          </p>
                        )}
                        <p className="font-medium text-primary">
                          {formatCurrency(payment.amount_paid || payment.amount || 0)}
                        </p>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={cn('text-xs gap-1', statusInfo.class)}>
                          <StatusIcon className="w-3 h-3" />
                          {statusInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadReceipt(payment.id)}
                          disabled={downloadingId === payment.id}
                          title="Télécharger le reçu"
                          data-testid={`download-receipt-${payment.id}`}
                        >
                          {downloadingId === payment.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
